// Display helpers for files: type tags, sizes, dates.

const KINDS = {
  folder: { tag: 'DIR', label: 'Folder', tone: 'bg-[#FCE9B8] text-[#6B4A00] dark:bg-[#3A2F12] dark:text-[#F7D58A]' },
  pdf: { tag: 'PDF', label: 'PDF document', tone: 'bg-[#F6E1DE] text-[#8E2A1F] dark:bg-[#3A1F1B] dark:text-[#F2B8AF]' },
  image: { tag: 'IMG', label: 'Image', tone: 'bg-[#E2E6F5] text-[#2C3A8A] dark:bg-[#1F2440] dark:text-[#B9C3F0]' },
  sheet: { tag: 'XLS', label: 'Spreadsheet', tone: 'bg-[#DDF0E6] text-[#1D5B3C] dark:bg-[#16301F] dark:text-[#A8DDBF]' },
  doc: { tag: 'DOC', label: 'Document', tone: 'bg-[#E4E9F2] text-[#24406E] dark:bg-[#1C2638] dark:text-[#AFC4E8]' },
  slides: { tag: 'PPT', label: 'Presentation', tone: 'bg-[#FBE7D6] text-[#8A3F07] dark:bg-[#3A2516] dark:text-[#F4C08F]' },
  video: { tag: 'VID', label: 'Video', tone: 'bg-[#EDE4F3] text-[#5B2C7A] dark:bg-[#2E2038] dark:text-[#D6B9EA]' },
  audio: { tag: 'AUD', label: 'Audio', tone: 'bg-[#EDE4F3] text-[#5B2C7A] dark:bg-[#2E2038] dark:text-[#D6B9EA]' },
  archive: { tag: 'ZIP', label: 'Archive', tone: 'bg-[#E6E9ED] text-[#3A424D] dark:bg-[#262B32] dark:text-[#C7CED7]' },
  cad: { tag: '3D', label: '3D / CAD', tone: 'bg-[#E1E6EE] text-[#2E3B4E] dark:bg-[#232B38] dark:text-[#BCC8DA]' },
  design: { tag: 'DSN', label: 'Design file', tone: 'bg-[#F7E3EC] text-[#7A2348] dark:bg-[#381B28] dark:text-[#EDB3CB]' },
  text: { tag: 'TXT', label: 'Text', tone: 'bg-[#E6E9ED] text-[#3A424D] dark:bg-[#262B32] dark:text-[#C7CED7]' },
  file: { tag: 'FILE', label: 'File', tone: 'bg-[#E6E9ED] text-[#3A424D] dark:bg-[#262B32] dark:text-[#C7CED7]' },
};

const EXT = {
  pdf: 'pdf',
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', heic: 'image', tif: 'image', tiff: 'image', svg: 'image', avif: 'image',
  xls: 'sheet', xlsx: 'sheet', csv: 'sheet', numbers: 'sheet', ods: 'sheet',
  doc: 'doc', docx: 'doc', pages: 'doc', odt: 'doc', rtf: 'doc',
  ppt: 'slides', pptx: 'slides', key: 'slides',
  mp4: 'video', mov: 'video', webm: 'video', avi: 'video', mkv: 'video',
  mp3: 'audio', wav: 'audio', m4a: 'audio', aac: 'audio', flac: 'audio',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive',
  step: 'cad', stp: 'cad', stl: 'cad', obj: 'cad', dwg: 'cad', dxf: 'cad', '3dm': 'cad', iges: 'cad', igs: 'cad', fbx: 'cad',
  psd: 'design', ai: 'design', indd: 'design', fig: 'design', sketch: 'design', eps: 'design',
  txt: 'text', md: 'text', json: 'text', xml: 'text',
};

export const extension = (name = '') => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

export const kindOf = (item) => {
  if (!item) return KINDS.file;
  if (item.kind === 'folder') return KINDS.folder;
  const ext = extension(item.name);
  const key = EXT[ext]
    || (item.mime?.startsWith('image/') && 'image')
    || (item.mime?.startsWith('video/') && 'video')
    || (item.mime?.startsWith('audio/') && 'audio')
    || 'file';
  const kind = KINDS[key];
  // Show the real extension when it is short and informative
  const tag = key !== 'folder' && ext && ext.length <= 4 ? ext.toUpperCase() : kind.tag;
  return { ...kind, key, tag };
};

/** What the in-app previewer can show. */
export const previewType = (item) => {
  if (!item || item.kind === 'folder') return null;
  const mime = item.mime || '';
  if (/^image\/(png|jpeg|gif|webp|avif)$/.test(mime)) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'text/plain') return 'text';
  return null;
};

export const formatSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
const fullFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((startOfToday - new Date(date.getFullYear(), date.getMonth(), date.getDate())) / 86400000);
  if (diffDays <= 0) return `Today, ${timeFmt.format(date)}`;
  if (diffDays === 1) return 'Yesterday';
  if (date.getFullYear() === now.getFullYear()) return dayFmt.format(date);
  return fullFmt.format(date);
};

export const formatDateTime = (iso) =>
  iso ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—';

export const initials = (name = '') =>
  name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

const AUDIT_LABELS = {
  'file.upload': 'uploaded',
  'file.download': 'downloaded',
  'file.preview': 'previewed',
  'zip.download': 'downloaded as .zip',
  'folder.create': 'created folder',
  'node.rename': 'renamed',
  'node.move': 'moved',
  'node.trash': 'moved to trash',
  'node.restore': 'restored',
  'node.purge': 'deleted forever',
  'access.grant': 'shared',
  'access.change': 'changed access on',
  'access.revoke': 'removed access on',
  'security.change': 'changed security on',
  'drive.create': 'created drive',
  'drive.admin_open': 'opened as admin',
  'drive.update': 'changed the colour of',
  'link.create': 'created a guest link to',
  'link.invite': 'invited guests to',
  'link.uninvite': 'removed guests from',
  'link.update': 'changed the guest link to',
  'link.revoke': 'turned off the guest link to',
  'guest.verify': 'confirmed their email for',
  'guest.download': 'downloaded',
  'guest.preview': 'previewed',
  'guest.zip': 'downloaded as .zip',
  'file.update': 'saved a new version of',
  'file.sync_download': 'synced to a computer',
  'device.link': 'signed in a computer:',
  'device.revoke': 'signed out a computer:',
  'desktop.release': 'published a desktop app version',
};

/**
 * `withObject: false` when the item is implied (its own activity list): drops a
 * dangling "to"/"on"/"from"/"of"/"for"/":" so it reads "created a guest link".
 */
export const auditLabel = (action, { withObject = true } = {}) => {
  const label = AUDIT_LABELS[action] || action;
  return withObject ? label : label.replace(/ (to|on|from|of|for)$|:$/, '');
};

/** Who did an audited action: a LampTwist person, or an external guest by email. */
export const auditActor = (event) => {
  if (event.actor?.name) return event.actor.name;
  const guest = event.detail?.guest;
  if (guest === 'anonymous') return 'A guest (open link)';
  return guest ? `${guest} (guest)` : 'Someone';
};
